/**
 * Half-open UTC range [start, end) for funnel time-series buckets, matching Query DSL time.start / time.end
 * (backend: timestamp >= start AND timestamp < end).
 * Chart `date` values come from time_bucket; when the API truncates to YYYY-MM-DD, that is the bucket start (UTC).
 *
 * @param {string} bucketDateInput - ISO date or datetime from chart
 * @param {'hour'|'day'|'week'|'month'} granularity
 * @returns {{ start: string, end: string } | null}
 */
export function funnelChartBucketRange(bucketDateInput, granularity = "day") {
  const raw = String(bucketDateInput || "").trim();
  if (!raw) return null;

  const g = ["hour", "day", "week", "month"].includes(granularity) ? granularity : "day";

  if (g === "hour" && (raw.length > 10 || raw.includes("T"))) {
    const start = new Date(raw);
    if (Number.isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    return { start: start.toISOString(), end: end.toISOString() };
  }

  const dayPrefix = raw.length >= 10 ? raw.slice(0, 10) : raw;
  const parts = dayPrefix.split("-");
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const mo = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;

  const start = new Date(Date.UTC(y, mo - 1, d, 0, 0, 0, 0));
  let end;
  if (g === "day" || g === "hour") {
    end = new Date(Date.UTC(y, mo - 1, d + 1, 0, 0, 0, 0));
  } else if (g === "week") {
    end = new Date(Date.UTC(y, mo - 1, d + 7, 0, 0, 0, 0));
  } else if (g === "month") {
    end = new Date(Date.UTC(y, mo, 1, 0, 0, 0, 0));
  } else {
    end = new Date(Date.UTC(y, mo - 1, d + 1, 0, 0, 0, 0));
  }
  return { start: start.toISOString(), end: end.toISOString() };
}

/** Short label for the users panel when scoped to one chart bucket. */
export function formatFunnelBucketPeriodLabel(bucketDateInput, granularity = "day") {
  const raw = String(bucketDateInput || "").trim();
  if (!raw) return "";
  try {
    const g = ["hour", "day", "week", "month"].includes(granularity) ? granularity : "day";
    let d;
    if (g === "hour" && (raw.length > 10 || raw.includes("T"))) {
      d = new Date(raw);
    } else {
      const dayPrefix = raw.length >= 10 ? raw.slice(0, 10) : raw;
      const [y, mo, day] = dayPrefix.split("-").map(Number);
      d = new Date(Date.UTC(y, mo - 1, day, 0, 0, 0, 0));
    }
    if (Number.isNaN(d.getTime())) return raw;
    if (g === "hour") {
      return d.toLocaleString("en-US", {
        timeZone: "UTC",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    }
    if (g === "month") {
      return d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", year: "numeric" });
    }
    if (g === "week") {
      return `Week of ${d.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" })}`;
    }
    return d.toLocaleDateString("en-US", {
      timeZone: "UTC",
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return raw;
  }
}

/**
 * Parse segment label from time-series breakdown ("A / B" from CONCAT_WS) into API breakdownValues.
 */
export function segmentLabelToBreakdownValues(segmentLabel, breakdownDimensionCount) {
  const n = Number(breakdownDimensionCount) || 0;
  if (n <= 0) return null;
  const s = segmentLabel != null ? String(segmentLabel) : "";
  if (n === 1) return [s];
  const parts = s.split(/\s*\/\s*/).map((x) => x.trim());
  if (parts.length === n) return parts;
  return [s];
}
